import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { RemoveErrorType, SetErrorType } from '../../../../../hooks/useErrors';
import {
  MaxContestsAllowedType,
  PartnerType,
  VT_PURCHASE_PROVIDER_NOT_INFORMED,
  VT_PURCHASE_PROVIDER_SAME_AS_SOURCE,
  VtProviderType,
} from '../../types';

interface UseGeneralTabI {
  setError: SetErrorType;
  removeError: RemoveErrorType;
}

export default function useGeneralTab({ setError, removeError }: UseGeneralTabI) {
  const [vtProvider, setVtProvider] = useState<VtProviderType>({ value: null, label: 'Selecione um fornecedor' });
  const [vtProviderOptions, setVtProviderOptions] = useState<VtProviderType[]>([{ value: null, label: 'Selecione um fornecedor' }]);
  const [vtPurchaseProvider, setVtPurchaseProvider] = useState<VtProviderType>({
    value: VT_PURCHASE_PROVIDER_NOT_INFORMED,
    label: 'Não informado',
  });
  const [hasCustomLogo, setHasCustomLogo] = useState(false);
  const [showTicketsAtNotOptimizedResults, setShowTicketsAtNotOptimizedResults] = useState(false);
  const [hasCustomTextAtLetter, setHasCustomTextAtLetter] = useState(false);
  const [customTextAtLetter, setCustomTextAtLetter] = useState('');

  const [partner, setPartner] = useState<PartnerType>({ value: null, label: 'Nenhum parceiro' });
  const [partnerOptions, setPartnerOptions] = useState<PartnerType[]>([{ value: null, label: 'Nenhum parceiro' }]);
  const [secondPartner, setSecondPartner] = useState<PartnerType>({ value: null, label: 'Nenhum parceiro' });
  const [directResult, setDirectResult] = useState(false);
  const [maxContestsAllowed, setMaxContestsAllowed] = useState<MaxContestsAllowedType>(1);
  const [defaultWorkplaceUsesIntegrationWs, setDefaultWorkplaceUsesIntegrationWs] = useState(true);
  const [changesWpDataByWs, setChangesWpDataByWs] = useState(false);
  const [usesLogoAtEmail, setUsesLogoAtEmail] = useState(false);

  const [isPartner, setIsPartner] = useState(false);

  const [canRequestVtItemRelation, setCanRequestVtItemRelation] = useState(false);

  const [particularities, setParticularities] = useState('');
  const [usaRodoviarioValZero, setUsaRodoviarioValZero] = useState(false);
  const [kmRodoviarioValZero, setKmRodoviarioValZero] = useState<number | string>(0);

  function handleCustomTextAtLetterChange(event: ChangeEvent<HTMLTextAreaElement>) {
    const currentValue = event.target.value;
    setCustomTextAtLetter(currentValue);

    if ((currentValue.length < 10 || !currentValue.includes(' ')) && hasCustomTextAtLetter) {
      setError({ field: 'customTextAtLetter', message: 'Por favor, insira um texto válido!'});
    } else {
      removeError('customTextAtLetter');
    }
  }

  function handleHasCustomTextAtLetterChange(hasCustomText: boolean) {
    if (hasCustomText) {
      setHasCustomTextAtLetter(true);
    } else {
      setHasCustomTextAtLetter(false);
      removeError('customTextAtLetter');
    }
  }

  const handleUsaRodoviarioValZeroChange = useCallback((value: boolean) => {
    setUsaRodoviarioValZero(value);
    if (!value) {
      setKmRodoviarioValZero(0);
      removeError('kmRodoviarioValZero');
    } else {
      setKmRodoviarioValZero(1);
    }
  }, [removeError]);

  const handleKmRodoviarioValZeroChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const numericValue = value === '' ? '' : parseInt(value, 10);
    setKmRodoviarioValZero(numericValue);

    if (usaRodoviarioValZero && (numericValue === '' || numericValue <= 0)) {
      setError({ field: 'kmRodoviarioValZero', message: 'Informe uma quilometragem válida (maior que 0)' });
    } else {
      removeError('kmRodoviarioValZero');
    }
  }, [usaRodoviarioValZero, setError, removeError]);

  useEffect(() => {
    if (
      !vtPurchaseProvider.value
      || vtPurchaseProvider.value === VT_PURCHASE_PROVIDER_NOT_INFORMED
      || vtPurchaseProvider.value === VT_PURCHASE_PROVIDER_SAME_AS_SOURCE
    ) {
      return;
    }

    const matchedOption = vtProviderOptions.find((option) => option.value === vtPurchaseProvider.value);
    if (matchedOption && matchedOption.label !== vtPurchaseProvider.label) {
      setVtPurchaseProvider((current) => ({ ...current, label: matchedOption.label }));
    }
  }, [vtProviderOptions, vtPurchaseProvider.value, vtPurchaseProvider.label]);

  const secondPartnerOptions = useMemo(() => {
    return partnerOptions.filter((opt) => !opt.value || opt.value !== partner.value);
  }, [partnerOptions, partner.value]);

  useEffect(() => {
    if (!partner.value) {
      if (secondPartner.value !== null) {
        setSecondPartner({ value: null, label: 'Nenhum parceiro' });
      }
    } else if (secondPartner.value === partner.value) {
      setSecondPartner({ value: null, label: 'Nenhum parceiro' });
    }
  }, [partner.value, secondPartner.value]);

  const isGeneralTabValid = useMemo(() => (
    ((!!hasCustomTextAtLetter && !!customTextAtLetter) || (!hasCustomTextAtLetter)) &&
    (!usaRodoviarioValZero || (usaRodoviarioValZero && kmRodoviarioValZero !== '' && Number(kmRodoviarioValZero) > 0))
  ), [customTextAtLetter, hasCustomTextAtLetter, usaRodoviarioValZero, kmRodoviarioValZero]);

  return {
    vtProvider,
    setVtProvider,
    vtProviderOptions,
    setVtProviderOptions,
    vtPurchaseProvider,
    setVtPurchaseProvider,
    hasCustomLogo,
    setHasCustomLogo,
    showTicketsAtNotOptimizedResults,
    setShowTicketsAtNotOptimizedResults,
    hasCustomTextAtLetter,
    setHasCustomTextAtLetter,
    handleHasCustomTextAtLetterChange,
    customTextAtLetter,
    setCustomTextAtLetter,
    handleCustomTextAtLetterChange,
    partner,
    setPartner,
    partnerOptions,
    setPartnerOptions,
    secondPartner,
    setSecondPartner,
    secondPartnerOptions,
    directResult,
    setDirectResult,
    maxContestsAllowed,
    setMaxContestsAllowed,
    isGeneralTabValid,
    defaultWorkplaceUsesIntegrationWs,
    setDefaultWorkplaceUsesIntegrationWs,
    changesWpDataByWs,
    setChangesWpDataByWs,
    isPartner,
    setIsPartner,
    usesLogoAtEmail,
    setUsesLogoAtEmail,
    canRequestVtItemRelation,
    setCanRequestVtItemRelation,
    particularities,
    setParticularities,
    usaRodoviarioValZero,
    setUsaRodoviarioValZero,
    handleUsaRodoviarioValZeroChange,
    kmRodoviarioValZero,
    setKmRodoviarioValZero,
    handleKmRodoviarioValZeroChange,
  };
}
